// CustomCursorStore — per-device persistence of the user's self-drawn 24×24
// cursor (custom-cursor-2026-09-19.md §2.3).
//
// **Purpose**: after `room.welcome`, `SessionProvider` reads this store and
// (if a cursor was saved) sends a single `member.cursor-change` so the
// server re-learns it for a fresh join (§2.3: "自分のカーソルは localStorage
// から復元し、welcome 受信後に member.cursor-change を 1 回送る"). The
// cursor editor writes here on 「決定」("save") and clears it on
// 「カーソルを外す」("remove").
//
// v1 is deliberately **localStorage only** (no account/email persistence,
// §2.1 スコープ表) — same rationale/shape as `AdminEmailStore.ts` /
// `AdminTokenStore.ts` (storage-injection for node-env tests, auto-probe +
// in-memory fallback for SSR/private-browsing/quota).
//
// Key is namespaced with a version suffix (`.v1`) per the design doc so a
// future payload shape change (e.g. a different cursor size) can bump the
// key instead of trying to migrate old values.

import { type CustomCursor, customCursorSchema } from '@oekaki/protocol';

const KEY = 'oekaki.customCursor.v1';

/** Minimal Storage shape this module uses (mirrors AdminEmailStore.ts). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CustomCursorStore {
  /** Persist the confirmed cursor. Idempotent. */
  save(cursor: CustomCursor): void;
  /**
   * Look up the saved cursor. Returns `null` if absent, storage is
   * unavailable, or the stored JSON fails `customCursorSchema` validation
   * (defensive against a corrupted / hand-edited value — never throws).
   */
  get(): CustomCursor | null;
  /** Remove the saved cursor ("カーソルを外す"). Idempotent. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${KEY}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

const createMemoryStorage = (): StorageLike => {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
  };
};

export interface CreateCustomCursorStoreOptions {
  storage?: StorageLike | null;
}

export const createCustomCursorStore = (
  opts: CreateCustomCursorStoreOptions = {},
): CustomCursorStore => {
  const explicit = opts.storage;
  const storage =
    explicit === null
      ? createMemoryStorage()
      : (explicit ?? probeBrowserStorage() ?? createMemoryStorage());

  return {
    save(cursor) {
      try {
        storage.setItem(KEY, JSON.stringify(cursor));
      } catch {
        // Quota / disabled storage. Swallow — UX 上は次回入室時に復元されない
        // だけで、今のセッションのカーソルは server 側に既に反映されている。
      }
    },
    get() {
      try {
        const raw = storage.getItem(KEY);
        if (raw === null) return null;
        const parsed: unknown = JSON.parse(raw);
        const result = customCursorSchema.safeParse(parsed);
        return result.success ? result.data : null;
      } catch {
        return null;
      }
    },
    clear() {
      try {
        storage.removeItem(KEY);
      } catch {
        // ignore
      }
    },
  };
};
